import { useEffect, type RefObject } from 'react'

type Props = {
  refOfPointToShow: RefObject<HTMLDivElement>
  stickyRef: RefObject<HTMLDivElement>
}

export default function useStickyHeaderTransform({ refOfPointToShow, stickyRef }: Props): void {
  useEffect(() => {
    if (!window?.IntersectionObserver) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (stickyRef.current) {
          // Whenever refOfPointToShow is hidden from view from above, we want to show stickyRef.
          if (entry.boundingClientRect.y < 0 && !entry.isIntersecting) {
            stickyRef.current.style.visibility = 'visible'
            stickyRef.current.style.transform = 'translateY(0%)'
            document.documentElement.style.setProperty('--sticky-header-height', `${stickyRef.current.offsetHeight}px`)
          } else {
            stickyRef.current.style.visibility = 'hidden'
            stickyRef.current.style.transform = 'translateY(-120%)'
            document.documentElement.style.removeProperty('--sticky-header-height')
          }
        }
      },
      { threshold: 1 }
    )

    if (refOfPointToShow.current) {
      observer.observe(refOfPointToShow.current)
    }

    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--sticky-header-height')
    }
  }, [refOfPointToShow, stickyRef])

  // Browser auto-scrolling occurs when dragging elements to screen edges.
  // The sticky header blocks this native behavior, so we hide it during drag
  // operations to allow normal scrolling. An example of this is sorting sources
  // on the sources page.
  function handleDragEnter(): void {
    if (stickyRef.current) stickyRef.current.style.transform = 'translateY(-120%)'
  }

  if (stickyRef.current) stickyRef.current.ondragover = handleDragEnter
}
